import { Component } from '@theme/component';
import { requestIdleCallback, viewTransition, yieldToMainThread } from '@theme/utilities';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';
import { StandardEvents } from '@shopify/events';
import { getScrollTop, scrollTo } from '@theme/scroll-container';

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {Map<number, Promise<void>>} */
  pageFetches = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {((value: void) => void) | null} */
  #resolveNextPagePromise = null;

  /** @type {((value: void) => void) | null} */
  #resolvePreviousPagePromise = null;

  /** @type {PaginatedListAspectRatioHelper} */
  #aspectRatioHelper;

  connectedCallback() {
    super.connectedCallback();

    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard,
      });
    }

    this.#fetchPage('next');
    this.#fetchPage('previous');
    this.#observeViewMore();

    // Listen for filter updates to clear cached pages
    document.addEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.addEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }
    // Remove the filter update listeners
    document.removeEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.removeEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
  }

  #observeViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.refs;

    // Return if neither element exists
    if (!viewMorePrevious && !viewMoreNext) return;

    // Create observer if it doesn't exist
    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          // Wait for any in-progress view transitions to finish
          if (viewTransition.current) await viewTransition.current;

          for (const entry of entries) {
            if (entry.isIntersecting) {
              // Use current refs to check which element triggered
              const { viewMorePrevious, viewMoreNext } = this.refs;

              if (entry.target === viewMorePrevious) {
                this.#renderPreviousPage();
              } else if (entry.target === viewMoreNext) {
                this.loadNextPage();
              }
            }
          }
        },
        {
          // Start close enough to the final row to avoid chaining two heavy
          // 20-product batches while the customer is still viewing the first.
          rootMargin: '450px 0px',
        }
      );
    }

    // Observe the view more elements
    if (viewMorePrevious) {
      this.infinityScrollObserver.observe(viewMorePrevious);
    }

    if (viewMoreNext) {
      this.infinityScrollObserver.observe(viewMoreNext);
    }
  }

  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo - The page info
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;

    return true;
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    const page = this.#getPage(type);

    // Always resolve the promise, even if we can't fetch the page
    const resolvePromise = () => {
      if (type === 'next') {
        this.#resolveNextPagePromise?.();
        this.#resolveNextPagePromise = null;
      } else {
        this.#resolvePreviousPagePromise?.();
        this.#resolvePreviousPagePromise = null;
      }
    };

    if (!page || !this.#shouldUsePage(page)) {
      // Resolve the promise even if we can't fetch
      resolvePromise();
      return false;
    }

    let pendingFetch = this.pageFetches.get(page.page);
    if (!pendingFetch) {
      pendingFetch = this.#fetchSpecificPage(page.page, page.url);
      this.pageFetches.set(page.page, pendingFetch);
    }

    try {
      await pendingFetch;
      return this.pages.has(page.page);
    } catch (error) {
      console.warn(`Unable to prefetch product page ${page.page}`, error);
      return false;
    } finally {
      if (this.pageFetches.get(page.page) === pendingFetch) this.pageFetches.delete(page.page);
      // A failed request must never leave the load-more control waiting forever.
      resolvePromise();
    }
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   */
  async #fetchSpecificPage(pageNumber, url = undefined) {
    const pageInfo = { page: pageNumber, url };

    if (!url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', pageNumber.toString());
      newUrl.hash = '';
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo)) return;
    let lastError;

    // Validate Shopify's response before caching it. A temporary CDN/challenge
    // page is HTML too, but it does not contain the requested product grid.
    for (let attempt = 0; attempt < 2; attempt += 1) {
      const requestUrl = new URL(pageInfo.url.toString());
      requestUrl.searchParams.set('section_id', this.sectionId.replace(/^shopify-section-/, ''));
      if (attempt > 0) requestUrl.searchParams.set('_mi_retry', Date.now().toString());
      requestUrl.searchParams.sort();

      const controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort(), 4500);

      try {
        const response = await fetch(requestUrl.toString(), {
          credentials: 'same-origin',
          // Collection section responses are URL-specific (page + filters), so
          // the browser/CDN cache is safe and makes prefetched batches instant.
          cache: 'default',
          headers: { Accept: 'text/html', 'X-Requested-With': 'XMLHttpRequest' },
          signal: controller.signal,
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const pageContent = await response.text();
        const parsedPage = new DOMParser().parseFromString(pageContent, 'text/html');
        const grid = parsedPage.querySelector('[ref="grid"]');
        const expectedCard = grid?.querySelector(`[ref="cards[]"][data-page="${pageNumber}"]`);
        if (!grid || !expectedCard) throw new Error('The requested product page was not returned');

        this.pages.set(pageNumber, pageContent);
        return;
      } catch (error) {
        lastError = error;
        if (attempt === 0) await new Promise((resolve) => window.setTimeout(resolve, 350));
      } finally {
        window.clearTimeout(timeout);
      }
    }

    throw lastError || new Error(`Unable to load product page ${pageNumber}`);
  }

  async #renderNextPage() {
    const { grid } = this.refs;

    if (!grid) return;

    const nextPage = this.#getPage('next');

    if (!nextPage || !this.#shouldUsePage(nextPage)) return;
    let nextPageItemElements = this.#getGridForPage(nextPage.page);

    if (!nextPageItemElements) {
      const loaded = await this.#fetchPage('next');
      if (!loaded) throw new Error(`Unable to load product page ${nextPage.page}`);
      nextPageItemElements = this.#getGridForPage(nextPage.page);
      if (!nextPageItemElements) throw new Error(`Product page ${nextPage.page} has no grid`);
    }

    const renderedProductIds = new Set(
      Array.from(grid.children, (item) => item instanceof HTMLElement ? item.dataset.productId : '').filter(Boolean)
    );
    const uniqueNextPageItems = Array.from(nextPageItemElements).filter((item) => {
      if (!(item instanceof HTMLElement)) return true;
      const productId = item.dataset.productId;
      if (!productId || !renderedProductIds.has(productId)) {
        if (productId) renderedProductIds.add(productId);
        return true;
      }
      return false;
    });

    grid.append(...uniqueNextPageItems);

    // Custom product cards do not always expose a cardGallery ref. The page
    // has already loaded successfully in that case, so skip ratio processing
    // instead of showing a false "failed to load" message.
    this.#aspectRatioHelper?.processNewElements();

    history.pushState('', '', nextPage.url.toString());

    requestIdleCallback(() => {
      this.#fetchPage('next');
    });
  }

  /**
   * Loads one additional page when an explicit "show more" control is used.
   * @param {Event} event
   */
  async loadNextPage(event) {
    const button = event?.currentTarget instanceof HTMLButtonElement ? event.currentTarget : this.refs.viewMoreNext;
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;

    const status = this.refs.loadMoreStatus;
    const label = this.refs.loadMoreLabel;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    if (label) label.textContent = 'جارٍ تحميل المنتجات…';
    if (status) status.textContent = 'يرجى الانتظار';

    try {
      await this.#renderNextPage();
      const nextPage = this.#getPage('next');
      const hasNextPage = this.#shouldUsePage(nextPage);

      if (!hasNextPage) {
        const wrapper = this.refs.viewMoreNextWrapper;
        // Stop observing before hiding the terminal control. The theme gives
        // `.mi-load-more` an explicit display value, so `hidden` also has a
        // dedicated CSS rule to guarantee that the finished loader disappears.
        this.infinityScrollObserver?.unobserve(button);
        if (label) label.textContent = 'تم عرض جميع المنتجات';
        if (wrapper instanceof HTMLElement) {
          wrapper.hidden = true;
          wrapper.setAttribute('aria-hidden', 'true');
        } else {
          button.hidden = true;
        }
        if (status) status.textContent = 'تم عرض جميع المنتجات';
      } else {
        button.disabled = false;
        if (label) label.textContent = 'عرض المزيد';
        const batchSize = Number.parseInt(button.dataset.batchSize || '20', 10) || 20;
        if (status) status.textContent = `تم تحميل ${batchSize} منتجًا إضافيًا`;
      }
    } catch (error) {
      button.disabled = false;
      if (label) label.textContent = 'إعادة المحاولة';
      if (status) status.textContent = 'تعذر تحميل المنتجات. حاول مرة أخرى.';
      console.error('Unable to load more products', error);
    } finally {
      button.removeAttribute('aria-busy');
    }
  }

  async #renderPreviousPage() {
    const { grid } = this.refs;

    if (!grid) return;

    const previousPage = this.#getPage('previous');
    if (!previousPage || !this.#shouldUsePage(previousPage)) return;

    let previousPageItemElements = this.#getGridForPage(previousPage.page);
    if (!previousPageItemElements) {
      const loaded = await this.#fetchPage('previous');
      if (!loaded) throw new Error(`Unable to load product page ${previousPage.page}`);
      previousPageItemElements = this.#getGridForPage(previousPage.page);
      if (!previousPageItemElements) throw new Error(`Product page ${previousPage.page} has no grid`);
    }

    // Store the current scroll position and height of the first element
    const currentScrollTop = getScrollTop();
    const firstElement = grid.firstElementChild;
    const oldHeight = firstElement ? firstElement.getBoundingClientRect().top + currentScrollTop : 0;

    // Prepend the new elements
    grid.prepend(...previousPageItemElements);

    this.#aspectRatioHelper?.processNewElements();

    // Calculate and adjust scroll position to maintain the same view
    if (firstElement) {
      const newHeight = firstElement.getBoundingClientRect().top + getScrollTop();
      const heightDiff = newHeight - oldHeight;
      scrollTo({
        top: currentScrollTop + heightDiff,
        behavior: 'instant',
      });
    }

    await yieldToMainThread();

    history.pushState('', '', previousPage.url.toString());

    requestIdleCallback(() => {
      this.#fetchPage('previous');
    });
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    // Read the live DOM here. Component refs can lag behind nodes appended by
    // manual pagination, which made some collections request page 2 forever.
    const cards = Array.from(this.querySelectorAll('[ref="cards[]"]'));
    const isPrevious = type === 'previous';

    if (cards.length === 0) return;

    // DOM reconciliation can place newly appended cards before existing ones,
    // so first/last child is not a reliable page boundary. Use the numeric
    // page range represented by all rendered cards.
    const loadedPages = cards.map((card) => Number(card.dataset.page)).filter(Number.isFinite);
    if (loadedPages.length === 0) return;
    const currentCardPage = isPrevious ? Math.min(...loadedPages) : Math.max(...loadedPages);
    const page = isPrevious ? currentCardPage - 1 : currentCardPage + 1;

    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return {
      page,
      url,
    };
  }

  /**
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    return gridElement.querySelectorAll(':scope > [ref="cards[]"]');
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * Handle filter updates by clearing cached pages.
   * Only reacts to events from the same section (ignores e.g. predictive search in the header).
   * @param {Event} event
   */
  #handleFilterUpdate = (event) => {
    const eventSection = /** @type {Element | null} */ (event.target)?.closest('[id^="shopify-section-"]');
    if (eventSection && eventSection.id !== `shopify-section-${this.sectionId}`) return;
    this.pages.clear();
    this.pageFetches.clear();

    // Resolve any pending promises to unblock waiting renders
    this.#resolveNextPagePromise?.();
    this.#resolvePreviousPagePromise?.();

    this.#resolveNextPagePromise = null;
    this.#resolvePreviousPagePromise = null;

    // Store the current lastPage value to detect when it changes
    const currentLastPage = this.refs.grid?.dataset.lastPage;

    // We need to wait for the DOM to be updated with the new filtered content
    // Using mutation observer to detect when the grid actually updates
    const observer = new MutationObserver(() => {
      // Check if data-last-page changed
      const newLastPage = this.refs.grid?.dataset.lastPage;

      if (newLastPage !== currentLastPage) {
        observer.disconnect();

        // Check if component is still connected
        if (!this.isConnected) {
          return;
        }

        // Now the DOM has been updated with the new filtered content
        this.#observeViewMore();

        // Fetch the next page
        this.#fetchPage('next');
      }
    });

    // Observe the grid for changes
    const { grid } = this.refs;
    if (grid) {
      observer.observe(grid, {
        attributes: true,
        attributeFilter: ['data-last-page'],
        childList: true, // Also watch for child changes in case the whole grid is replaced
      });

      // Set a timeout as a fallback in case the mutation never fires
      setTimeout(() => {
        if (observer) {
          observer.disconnect();
        }
      }, 3000);
    }
  };
}
